import type { UUID } from '@aline/common';
import type { ApolloLink } from '@apollo/client';
import { gql } from '@apollo/client';
import { uniqWith } from 'ramda';
import { Apollo } from 'src/gql/generated';
import { documentTypename } from 'src/helpers';

export function useCreateDocumentVariable(): {
  createVariable: (
    documentId: UUID,
    options: Parameters<Apollo.CreateDocumentVariableMutationHookResult[0]>[0]
  ) => Promise<ApolloLink.Result<Apollo.CreateDocumentVariableMutation>>;
} {
  const [createVariable] = Apollo.useCreateDocumentVariableMutation();

  return {
    createVariable: async (documentId, options) => {
      return createVariable({
        ...options,
        update: (cache, { data }) => {
          cache.modify({
            fields: {
              variables: (refs = [], { readField }) => {
                const newRef = cache.writeFragment({
                  data: data?.createVariable,
                  fragment: gql`
                    fragment NewVariable on DocumentVariable {
                      __typename
                      id
                    }
                  `,
                });

                return uniqWith(
                  (a, b) => readField<UUID>('id', a) === readField<UUID>('id', b),
                  [...refs, newRef]
                );
              },
            },
            id: cache.identify({ __typename: documentTypename, id: documentId }),
          });
        },
      });
    },
  };
}

/**
 * Get DocumentVariables for a given Document.
 */
export function useGetVariablesForDocument(
  documentId: UUID,
  options?: Omit<
    NonNullable<Parameters<typeof Apollo.useGetVariablesForDocumentQuery>[0]>,
    'variables'
  >
): {
  loading: boolean;
  variables: NonNullable<
    Apollo.GetVariablesForDocumentQueryResult['data']
  >['document']['variables'];
} {
  const { data, loading } = Apollo.useGetVariablesForDocumentQuery({
    ...options,
    variables: { documentId },
  });

  return {
    loading,
    variables: data?.document.variables ?? [],
  };
}

export function useDisableVariable(): {
  disableVariable: (
    id: UUID,
    options: Parameters<Apollo.DisableDocumentVariableMutationHookResult[0]>[0]
  ) => Promise<ApolloLink.Result<Apollo.DisableDocumentVariableMutation>>;
} {
  const [disableVariable] = Apollo.useDisableDocumentVariableMutation();

  return {
    disableVariable: (id, options) => {
      return disableVariable({
        ...options,
        optimisticResponse: {
          __typename: 'Mutation',
          disableVariable: {
            __typename: 'DocumentVariable',
            deletedAt: new Date().toISOString(),
            id,
          },
        },
      });
    },
  };
}
